import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { useTellescopeMutation } from "@tellescope/tanstack-query";
import { useSendChatMessage } from "../../hooks/useSendChatMessage";
import { useEnduserStore } from "../../store/hooks";

const EMPTY_CARE_TEAM: string[] = [];

interface UseNewConversationDialogProps {
  onOpenChange: (open: boolean) => void;
  onConversationCreated: () => void;
  allowCareTeamSelection?: boolean;
}

export const useNewConversationDialog = ({
  onOpenChange,
  onConversationCreated,
  allowCareTeamSelection,
}: UseNewConversationDialogProps) => {
  const { t } = useTranslation("messages");
  const session = useEnduserStore((state) => state.session);
  const enduserId = useEnduserStore((state) => state.currentEnduser?.id);
  const careTeamUserIds = useEnduserStore((state) => state.currentEnduser?.assignedTo) ?? EMPTY_CARE_TEAM;

  const requiresCareTeamSelection = useMemo(
    () => !!allowCareTeamSelection && careTeamUserIds.length > 0,
    [allowCareTeamSelection, careTeamUserIds.length]
  );

  const createRoomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "createOne",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "invalidate",
  });

  const { sendMessageAsync, isPending: isSendingMessage } = useSendChatMessage();
  const isPending = createRoomMutation.isPending || isSendingMessage;

  const form = useForm({
    fields: {
      subject: { type: "text", initialValue: "", required: true },
      initialMessage: { type: "textarea", initialValue: "", required: true },
      selectedCareTeamUserId: { type: "select", initialValue: "" },
    },
    validators: {
      subject: (value) => (value.trim() ? undefined : "required"),
      initialMessage: (value) => (value.trim() ? undefined : "required"),
      selectedCareTeamUserId: (value) => (requiresCareTeamSelection && !value ? "required" : undefined),
    },
    resetOnSubmit: false,
  });

  const canSubmit = form.isValid && !isPending;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        form.resetForm();
      }
      onOpenChange(newOpen);
    },
    [onOpenChange, form]
  );

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !enduserId) return;

    const { subject, initialMessage, selectedCareTeamUserId } = form.values;

    try {
      const createdRoom = await createRoomMutation.mutateAsync({
        title: subject.trim(),
        enduserIds: [enduserId],
        ...(requiresCareTeamSelection && selectedCareTeamUserId ? { userIds: [selectedCareTeamUserId] } : {}),
      });

      await sendMessageAsync({
        roomId: createdRoom.id,
        message: initialMessage.trim(),
      });

      form.resetForm();
      onOpenChange(false);
      onConversationCreated();
    } catch {
      // Errors handled by mutation state
    }
  }, [
    canSubmit,
    enduserId,
    form,
    requiresCareTeamSelection,
    createRoomMutation,
    sendMessageAsync,
    onOpenChange,
    onConversationCreated,
  ]);

  return {
    t,
    form,
    isPending,
    canSubmit,
    handleOpenChange,
    handleSubmit,
    careTeamUserIds,
    requiresCareTeamSelection,
  };
};
